import React, { useEffect, useState, useRef, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BookUser, Upload, Loader2, ShieldCheck, Phone, Search, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { PredictionBadge, StatusBadge } from "@/components/badges";
import VoterEditDialog from "@/components/VoterEditDialog";

const STORAGE_KEY = "imeak_contact_matches";

// Rehber dosyasından (VCF/CSV/TXT) telefon numaralarını çıkarır. Kişi isimleri kullanılmaz.
function extractPhones(text) {
  const out = new Set();
  const candidates = text.match(/[+(]?\d[\d\s()\-.]{6,}\d/g) || [];
  for (const c of candidates) {
    const digits = c.replace(/\D/g, "");
    if (digits.length >= 10) out.add(digits.slice(-10));
  }
  return [...out];
}

export default function ContactsMatch() {
  const fileRef = useRef();
  const [parsing, setParsing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [search, setSearch] = useState("");
  const [team, setTeam] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    api.get("/meta").then(({ data }) => { setTeam(data.team_members || []); if (data.sectors) setSectors(data.sectors); }).catch(() => {});
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (cached) setResult(JSON.parse(cached));
    } catch { /* ignore */ }
  }, []);

  const persist = (data) => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
  };

  const clearResult = () => {
    setResult(null);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setParsing(true);
    try {
      const text = await file.text();
      const phones = extractPhones(text);
      if (phones.length === 0) {
        toast.error("Dosyada telefon numarası bulunamadı. VCF veya CSV rehber dosyası yükleyin.");
        return;
      }
      setLoading(true);
      const { data } = await api.post("/contacts/match", { phones });
      const payload = { matches: data.matches || [], total_contacts: data.total_contacts, matched_count: data.matched_count };
      setResult(payload);
      persist(payload);
      toast.success(`${data.total_contacts} numara tarandı, ${data.matched_count} firma eşleşti`);
    } catch (err) {
      toast.error("Rehber okunamadı veya eşleştirilemedi");
    } finally {
      setParsing(false);
      setLoading(false);
    }
  };

  const matches = useMemo(() => result?.matches || [], [result]);
  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return matches;
    return matches.filter((m) =>
      (m.company_name || "").toLocaleLowerCase("tr").includes(q) ||
      (m.contact_person || "").toLocaleLowerCase("tr").includes(q) ||
      (m.partner_info || "").toLocaleLowerCase("tr").includes(q) ||
      (m.phone || "").includes(q));
  }, [matches, search]);

  return (
    <div className="space-y-5" data-testid="contacts-match-page">
      <div>
        <h1 className="text-3xl font-display font-bold text-foreground flex items-center gap-2">
          <BookUser className="h-7 w-7 text-[#0F2C59] dark:text-blue-300" /> Rehberimdeki Firmalar
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Telefon rehberinizi (VCF / CSV) yükleyin; sistem, rehberinizde kayıtlı olan telefon numaralarına sahip firmaları listeler.
          Eşleştirme yalnız <span className="font-semibold">telefon numarasına</span> göre yapılır.
        </p>
      </div>

      <Card className="p-4 border border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20 flex items-start gap-3" data-testid="contacts-privacy-note">
        <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
        <p className="text-xs text-emerald-800 dark:text-emerald-300 leading-relaxed">
          Gizlilik: Rehber dosyanız cihazınızda (tarayıcıda) çözümlenir; sunucuya yalnızca telefon numaraları gönderilir, kişi isimleri gönderilmez.
          Hiçbir rehber verisi sistemde kalıcı saklanmaz — eşleşmeler yalnız bu oturum boyunca sizin ekranınızda tutulur.
        </p>
      </Card>

      <div className="flex items-center gap-2 flex-wrap">
        <input ref={fileRef} type="file" accept=".vcf,.csv,.txt,text/vcard,text/csv" className="hidden" onChange={onFile} data-testid="contacts-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={parsing || loading} className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="contacts-upload-button">
          {(parsing || loading) ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
          Rehber Dosyası Yükle (VCF / CSV)
        </Button>
        {result && (
          <Button variant="outline" size="sm" onClick={clearResult} data-testid="contacts-clear-button">
            <Trash2 className="h-4 w-4 mr-2" />Sonucu Temizle
          </Button>
        )}
      </div>

      {result && (
        <div className="grid grid-cols-2 gap-3 sm:max-w-md">
          <Card className="p-4" data-testid="contacts-stat-scanned">
            <p className="text-xs text-muted-foreground">Taranan Numara</p>
            <p className="text-2xl font-bold text-[#0F2C59] dark:text-blue-300">{result.total_contacts}</p>
          </Card>
          <Card className="p-4" data-testid="contacts-stat-matched">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" /> Eşleşen Firma</p>
            <p className="text-2xl font-bold text-emerald-600">{result.matched_count}</p>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>
      ) : !result ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="contacts-empty-initial">
          Henüz rehber yüklemediniz. Yukarıdan telefon rehberinizi yükleyerek başlayın.
        </Card>
      ) : matches.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="contacts-empty-result">
          Rehberinizdeki numaralarla eşleşen firma bulunamadı.
        </Card>
      ) : (
        <>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Firma / kişi / telefon ara…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="contacts-search" />
          </div>
          <div className="space-y-2" data-testid="contacts-match-list">
            {filtered.map((m, idx) => (
              <Card key={m.id} className="border border-border p-4 hover:bg-muted/30 cursor-pointer" onClick={() => setEditId(m.id)} data-testid={`contacts-match-${idx}`}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate" data-testid={`contacts-match-name-${idx}`}>{m.company_name}</p>
                    {m.contact_person && <p className="text-xs text-muted-foreground truncate">Yetkili: {m.contact_person}</p>}
                    {m.partner_info && <p className="text-xs text-muted-foreground truncate">Ortak: {m.partner_info}</p>}
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Komite #{m.committee_no} · {m.committee_name}
                    </p>
                    {m.phone && <p className="text-xs inline-flex items-center gap-1 mt-1 text-foreground"><Phone className="h-3 w-3 text-emerald-600" />{m.phone}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <StatusBadge value={m.status} />
                    <PredictionBadge value={m.current_vote_prediction_2026} />
                  </div>
                </div>
              </Card>
            ))}
            {filtered.length === 0 && <p className="text-center py-8 text-muted-foreground">Aramaya uygun sonuç yok</p>}
          </div>
        </>
      )}

      <VoterEditDialog voterId={editId} open={!!editId} onClose={() => setEditId(null)} onSaved={() => {}} team={team} sectors={sectors} />
    </div>
  );
}
